/**
 * Pure routing-decision logic for the manual vs. auto macro-setup onboarding
 * flow, extracted so it's testable without rendering (MacroSetupChoice,
 * GoalSelection, MacroQuiz, and ManualMacros all import from here).
 *
 * Flow (pre-launch body/profile-collection patch):
 *   Auto:   MacroSetupChoice -> GoalSelection -> MacroQuiz [auto mode]
 *           -> backend V4 target generation -> Allergens
 *   Manual: MacroSetupChoice -> GoalSelection [manual mode] -> MacroQuiz
 *           [manual mode, body/profile questions only] -> ManualMacros
 *           -> Allergens
 *
 * Both modes now go through the SAME body/activity questionnaire
 * (MacroQuiz) so manual users end onboarding with a complete profile too —
 * the difference between modes is only who sets the nutrition targets:
 * manual mode never calls backend target regeneration and never overwrites
 * the four targets the user enters themselves in ManualMacros. Manual mode
 * still exists as a location-state tag (mode: 'manual') so a goal is ALWAYS
 * captured before onboarding completes — previously the manual path skipped
 * GoalSelection entirely, which could leave a profile with real macro
 * targets but no goal.
 */

/** Route MacroSetupChoice's "Generate my plan" option navigates to. */
export const AUTO_SETUP_ROUTE = { path: '/onboarding/goal', state: undefined };

/** Route MacroSetupChoice's "Enter manually" option navigates to. */
export const MANUAL_SETUP_ROUTE = { path: '/onboarding/goal', state: { mode: 'manual' } };

/** Route ManualMacros' Back button navigates to — back into the shared
 * body/profile questionnaire (still tagged manual mode), not the auto
 * flow's goal screen. */
export const MANUAL_MACROS_BACK_ROUTE = { path: '/onboarding/quiz', state: { mode: 'manual' } };

/** True when GoalSelection/MacroQuiz was entered via the manual-macros path. */
export function isManualGoalMode(locationState) {
  return locationState?.mode === 'manual';
}

/**
 * Where GoalSelection navigates to after successfully saving the user's
 * goal. Both modes now go through the same body/profile questionnaire
 * (MacroQuiz) — manual mode carries the `mode: 'manual'` tag forward so
 * MacroQuiz knows never to call backend target generation for it.
 */
export function goalSelectionNextRoute(locationState, backendGoal, extraState = {}) {
  const manual = isManualGoalMode(locationState);
  return {
    path: '/onboarding/quiz',
    state: { goal: backendGoal, ...(manual ? { mode: 'manual' } : {}), ...extraState },
  };
}

/**
 * Where MacroQuiz navigates after collecting body/profile info. Manual mode
 * still needs the user's own four macro targets (ManualMacros) and must
 * never have generated targets computed for it; auto mode has already had
 * the backend generate targets by this point and proceeds straight to
 * Allergens, same as before.
 */
export function quizNextRoute(locationState, extraState = {}) {
  const manual = isManualGoalMode(locationState);
  return {
    path: manual ? '/onboarding/manual-macros' : '/onboarding/allergens',
    state: extraState,
  };
}

/**
 * Whether a failed backend goal save should block onboarding from
 * continuing (nutrition-readiness follow-up, AGENTS.md pre-launch audit).
 *
 * Only a real authenticated (non-demo) account has a backend profile that
 * must actually be persisted — /smart-fill/auto correctly fails closed on a
 * missing goal, so silently continuing past a real save failure here would
 * just move the problem downstream to a confusing planning-time error
 * instead of letting the user retry now. Demo/local-only accounts have no
 * backend profile to save to, so they continue with local state as before.
 */
export function shouldBlockOnboardingOnGoalSaveFailure(isAuthenticated, isDemoMode) {
  return !!isAuthenticated && !isDemoMode;
}

/**
 * GoalSelection's store update: MERGE the goal fields into the existing
 * onboarding profile. Replacing the whole profile erased everything entered
 * earlier or later in the flow (body info, activity, allergens/dislikes and
 * the food-preference draft) when the user went Back through the goal step.
 */
export function mergeGoalIntoProfile(profile, goalData) {
  return { ...(profile || {}), ...(goalData || {}) };
}
